import { trigger, state, style, transition, animate, AnimationTriggerMetadata, sequence } from '@angular/animations';

export enum AnimationTimings {
    Enter = '300ms ease-in',
    Leave = '300ms ease-out',
    Linear = '300ms linear',
}

export enum FadeTimings {
    In = '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    Out = '50ms cubic-bezier(0.4, 0, 1, 1)',
    Linear = '300ms linear',
}

export enum ExpandAnimationState {
    Collapsed = 'collapsed',
    Expanded = 'expanded',
    NoAnimation = 'no-animation',
}

export enum FadeAnimationState {
    In = 'in',
    Out = 'out',
    NoAnimation = 'no-animation',
}

export enum SizeAnimationState {
    Initial = 'initial',
    Applied = 'applied',
    NoAnimation = 'no-animation',
}

export enum PositionAnimationState {
    Default = 'default',
    Moved = 'moved',
    NoAnimation = 'no-animation',
}

export const expandAnimation = (
    propertyToChange: 'width' | 'height' | 'margin-top' | 'margin-bottom' | 'margin-left' | 'margin-right' = 'height',
    heightInPixels: number = 0,
    animationTiming: AnimationTimings = AnimationTimings.Linear
): AnimationTriggerMetadata =>
    trigger('expand', [
        state(ExpandAnimationState.Collapsed, style({ [propertyToChange]: `${heightInPixels}px` })),
        state(ExpandAnimationState.Expanded, style({ [propertyToChange]: '*' })),
        state(ExpandAnimationState.NoAnimation, style({ [propertyToChange]: '*' })),
        transition(`${ExpandAnimationState.Collapsed} <=> ${ExpandAnimationState.Expanded}`, animate(animationTiming)),
        transition(`${ExpandAnimationState.NoAnimation} => ${ExpandAnimationState.Collapsed}`, []),
        transition(`${ExpandAnimationState.NoAnimation} => ${ExpandAnimationState.Expanded}`, []),
    ]);

export const fadeAnimation = (
    fadeInTiming: FadeTimings = FadeTimings.In,
    fadeOutTiming: FadeTimings = FadeTimings.Out
): AnimationTriggerMetadata =>
    trigger('fade', [
        state(FadeAnimationState.In, style({ opacity: 1 })),
        state(FadeAnimationState.Out, style({ opacity: 0 })),
        state(FadeAnimationState.NoAnimation, style({ opacity: 1 })),
        transition(`${FadeAnimationState.Out} => ${FadeAnimationState.In}`, [animate(fadeInTiming)]),
        transition(`${FadeAnimationState.In} => ${FadeAnimationState.Out}`, [animate(fadeOutTiming)]),
        transition(`${FadeAnimationState.NoAnimation} => ${FadeAnimationState.In}`, [animate(fadeInTiming)]),
        transition(`${FadeAnimationState.NoAnimation} => ${FadeAnimationState.Out}`, [animate(fadeOutTiming)]),
    ]);

export const fadeWithDisplayAnimation = (
    fadeInTiming: string | FadeTimings = FadeTimings.In,
    fadeOutTiming: string | FadeTimings = FadeTimings.Out
): AnimationTriggerMetadata =>
    trigger('fadeWithDisplay', [
        transition(`${FadeAnimationState.Out} => ${FadeAnimationState.In}`, [
            style({ transform: 'scale(1)' }),
            animate(fadeInTiming, style({ opacity: 1 })),
        ]),
        transition(
            `${FadeAnimationState.In} => ${FadeAnimationState.Out}`,
            sequence([animate(fadeOutTiming, style({ opacity: 0 })), style({ transform: 'scale(0)' })])
        ),
        transition(`${FadeAnimationState.NoAnimation} => ${FadeAnimationState.In}`, [
            style({ transform: 'scale(1)' }),
            animate(fadeInTiming, style({ opacity: 1 })),
        ]),
        transition(
            `${FadeAnimationState.NoAnimation} => ${FadeAnimationState.Out}`,
            sequence([animate(fadeOutTiming, style({ opacity: 0 })), style({ transform: 'scale(0)' })])
        ),
    ]);

export const backgroundAnimation = (
    animationTiming: AnimationTimings = AnimationTimings.Linear
): AnimationTriggerMetadata =>
    trigger('background', [
        state('default', style({ backgroundColor: 'transparent', boxShadow: 'none' })),
        state(
            'white',
            style({
                backgroundColor: 'white',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            })
        ),
        transition('default <=> white', animate(animationTiming)),
    ]);

export const sphereExpandAnimation = (
    animationTiming: AnimationTimings = AnimationTimings.Leave
): AnimationTriggerMetadata =>
    trigger('sphereExpand', [
        state('collapsed', style({ scale: 0 })),
        state('expanded', style({ scale: 1 })),
        transition('collapsed <=> expanded', animate(animationTiming)),
    ]);

export const diagonalExpandAnimation = (
    animationTiming: AnimationTimings = AnimationTimings.Linear
): AnimationTriggerMetadata =>
    trigger('diagonalExpand', [
        state(
            ExpandAnimationState.Collapsed,
            style({
                transform: 'scaleX(0) scaleY(0)',
                opacity: 0,
                transformOrigin: 'bottom right',
            })
        ),
        state(
            ExpandAnimationState.Expanded,
            style({
                transform: 'scaleX(1) scaleY(1)',
                opacity: 1,
                transformOrigin: 'bottom right',
            })
        ),
        transition(`${ExpandAnimationState.Collapsed} <=> ${ExpandAnimationState.Expanded}`, [
            animate(animationTiming),
        ]),
    ]);

export const expandAnimationWithValue = (
    propertyToAnimate: string = 'height',
    closedValueInPixels: number = 0,
    openValueInPixels: number = 0,
    animationTiming: AnimationTimings = AnimationTimings.Linear
): AnimationTriggerMetadata =>
    trigger('expandWithValue', [
        state(
            ExpandAnimationState.Collapsed,
            style({
                [propertyToAnimate]: `${closedValueInPixels}px`,
                transformOrigin: 'right',
            })
        ),
        state(
            ExpandAnimationState.Expanded,
            style({
                [propertyToAnimate]: `${openValueInPixels}px`,
                transformOrigin: 'right',
            })
        ),
        transition(`${ExpandAnimationState.Collapsed} <=> ${ExpandAnimationState.Expanded}`, animate(animationTiming)),
    ]);

export const positionAnimation = (
    positionAttribute: 'top' | 'left' | 'right' | 'bottom',
    valueInPixels: number,
    animationTiming: AnimationTimings = AnimationTimings.Leave
): AnimationTriggerMetadata =>
    trigger('position', [
        state(PositionAnimationState.Default, style({ [positionAttribute]: '*' })),
        state(PositionAnimationState.Moved, style({ [positionAttribute]: '{{ deslocamento }}px' }), {
            params: { deslocamento: valueInPixels },
        }),
        state(PositionAnimationState.NoAnimation, style({ [positionAttribute]: '*' })),
        transition(`${PositionAnimationState.Default} <=> ${PositionAnimationState.Moved}`, animate(animationTiming)),
    ]);

export const sizeAnimation = (
    propertyToChange: 'width' | 'height' | 'margin-top' | 'margin-bottom' | 'margin-left' | 'margin-right' = 'height',
    heightInPixels: number = 0,
    animationTiming: AnimationTimings = AnimationTimings.Linear
): AnimationTriggerMetadata =>
    trigger('size', [
        state(SizeAnimationState.Initial, style({ [propertyToChange]: `${heightInPixels}px` })),
        state(SizeAnimationState.Applied, style({ [propertyToChange]: '*' })),
        state(SizeAnimationState.NoAnimation, style({ [propertyToChange]: '*' })),
        transition(`${SizeAnimationState.Initial} <=> ${SizeAnimationState.Applied}`, animate(animationTiming)),
    ]);
